import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { EditorInlineImageUploadResult } from '@kula-ai/components';
import { assertResolvedApiNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/http';

type UserImageUploadResponseBody = {
  image_url?: string;
  metadata?: {
    resolution?: { width?: number; height?: number };
  };
};

type UserImageUploadEnvelope = {
  data?: UserImageUploadResponseBody;
  errors?: unknown[];
};

/**
 * POST `/api/v1/user_image_uploads` (multipart `file`) — same contract as RTK `uploadInlineImage`.
 * Maps `image_url` + optional resolution into {@link EditorInlineImageUploadResult}.
 */
export async function uploadUserInlineImage(
  file: File
): Promise<EditorInlineImageUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const rawUnknown = await http.post('/user_image_uploads', formData);
  assertResolvedApiNotHttpError(rawUnknown);
  const raw = rawUnknown as
    | UserImageUploadEnvelope
    | UserImageUploadResponseBody;

  const inner =
    raw && typeof raw === 'object' && 'data' in raw && raw.data
      ? raw.data
      : (raw as UserImageUploadResponseBody);

  const imageUrl =
    typeof inner?.image_url === 'string' ? inner.image_url.trim() : '';
  if (!imageUrl) {
    throw new Error('upload_missing_image_url');
  }

  const w = inner.metadata?.resolution?.width;
  const h = inner.metadata?.resolution?.height;

  return {
    imageUrl,
    ...(typeof w === 'number' && Number.isFinite(w) ? { width: w } : {}),
    ...(typeof h === 'number' && Number.isFinite(h) ? { height: h } : {}),
  };
}

export function useUploadUserInlineImage(): UseMutationResult<
  EditorInlineImageUploadResult,
  Error,
  File
> {
  return useMutation({
    mutationFn: uploadUserInlineImage,
  });
}
